<template>
    <v-dialog :value="show" width="640" persistent scrollable>
        <div class="mu" @dragenter="onDragEnter" @dragover="onDragOver"
            @dragleave="onDragLeave" @drop="onDrop">
            <div v-if="dragDepth > 0" class="mu-drop-overlay" role="status">
                <span>{{ $t('media.upload.drop_release') }}</span>
            </div>
            <div class="mu-head">
                <div>
                    <div class="mu-title">
                        {{ isYoutube ? $t('media.upload.title_youtube') : $t('media.upload.title') }}
                    </div>
                    <div class="mu-sub">{{ purposeLabel }}</div>
                </div>
                <v-btn icon small :disabled="isUploading" @click="close">
                    <v-icon size="20">mdi-close</v-icon>
                </v-btn>
            </div>

            <div class="mu-body">
                <!-- ⚠ 字表不能上傳（沒有檔案，內容在 attributes.words）。理論上呼叫端
                     不該開這個對話框，但擋一次比讓老師選完檔案才被後端 400 好。 -->
                <div v-if="!uploadable" class="mu-blocked">
                    <v-icon size="30" color="#e57373">mdi-information-outline</v-icon>
                    <div>{{ $t('media.upload.not_uploadable') }}</div>
                </div>

                <template v-else>
                    <!-- ── YouTube 網址 ─────────────────────────────────── -->
                    <div v-if="isYoutube" class="mu-field">
                        <div class="mu-lbl">
                            {{ $t('media.upload.yt_url') }}<span class="mu-req">*</span>
                        </div>
                        <v-text-field v-model="youtubeUrl" dense outlined hide-details
                            :disabled="isUploading" placeholder="https://www.youtube.com/watch?v=..."
                            @input="fileError = ''" />
                        <div class="mu-hint">{{ $t('media.upload.yt_hint') }}</div>
                        <div v-if="fileError" class="mu-err" role="alert">{{ fileError }}</div>
                    </div>

                    <!-- ── 檔案 ─────────────────────────────────────────── -->
                    <div v-else class="mu-field">
                        <div class="mu-lbl">{{ $t('media.upload.file') }}</div>
                        <div class="mu-drop" :class="{ 'mu-drop--disabled': isUploading }">
                            <div class="mu-drop-hint">{{ $t('media.upload.drop_hint') }}</div>
                            <div class="mu-file">
                                <v-btn outlined small class="normal-case" :disabled="isUploading"
                                    @click="pickFile">
                                    {{ file ? $t('media.upload.file_change') : $t('media.upload.file_pick') }}
                                </v-btn>
                                <span v-if="file" class="mu-file-name">{{ file.name }}</span>
                                <span v-else class="mu-hint">{{ $t('media.upload.file_none') }}</span>
                            </div>
                        </div>
                        <div v-if="isValidating" class="mu-hint" role="status">
                            {{ $t('media.upload.validating') }}
                        </div>
                        <div class="mu-hint">{{ limitHint }}</div>
                        <!-- 刻意**不提供貼網址**：外部圖片有版權疑慮（2026-08-12 決定）。
                             YouTube 影片是唯一例外，走另一支端點。 -->
                        <div v-if="fileError" class="mu-err" role="alert">{{ fileError }}</div>
                        <input ref="fileInput" class="d-none" type="file" :accept="accept"
                            @change="onFilePicked">
                    </div>

                    <!-- ── 熱區框選（只有找不同）───────────────────────── -->
                    <!-- ⚠ **`hotspot` 一定要框一個熱區**（2026-09-04）。在這之前
                         `isUploadable('hotspot')` 就已經是 true 而後端沒擋，所以老師
                         上傳得成功、挑得到，但存題目時被「這一關的圖沒有熱區」擋住
                         ——而且**沒有任何辦法補上座標**。那條死路現在被這個欄位填掉了。
                         ⚠ 熱區屬於素材而不是題目（ADR 0004）：座標與那張圖綁死，
                         換一張圖全部失效。所以在**上傳時**框，不是在題目裡框。 -->
                    <div v-if="isHotspot" class="mu-field">
                        <div class="mu-lbl">
                            {{ $t('media.hotspot.label') }}<span class="mu-req">*</span>
                        </div>
                        <HotspotPicker v-if="previewUrl" v-model="hotspotRect"
                            :src="previewUrl" />
                        <div v-else class="mu-hint">{{ $t('media.hotspot.pick_file_first') }}</div>
                    </div>

                    <!-- ── 標題 / 說明 ──────────────────────────────────── -->
                    <div class="mu-field">
                        <div class="mu-lbl">
                            {{ $t('media.upload.name') }}
                            <span class="mu-req">*</span>
                        </div>
                        <v-text-field v-model="title" dense outlined hide-details
                            :maxlength="TITLE_MAX" :disabled="isUploading"
                            :placeholder="$t('media.upload.name_ph')" />
                        <div class="mu-cnt">{{ title.length }} / {{ TITLE_MAX }}</div>
                    </div>

                    <div class="mu-field">
                        <div class="mu-lbl">{{ $t('media.upload.desc') }}</div>
                        <v-textarea v-model="description" dense outlined hide-details rows="2"
                            :maxlength="DESC_MAX" :disabled="isUploading"
                            :placeholder="$t('media.upload.desc_ph')" />
                        <div class="mu-cnt">{{ description.length }} / {{ DESC_MAX }}</div>
                    </div>

                    <!-- ── 分享範圍 ─────────────────────────────────────── -->
                    <div class="mu-field">
                        <div class="mu-lbl">{{ $t('media.upload.scope') }}</div>
                        <!-- ⚠ 只有兩個選項，**沒有「團隊」**——team 共用已於 2026-08-20 取消 -->
                        <v-radio-group v-model="shareScope" hide-details dense class="mu-radios"
                            :disabled="isUploading">
                            <v-radio v-for="s in scopes" :key="s" :value="s">
                                <template #label>
                                    <div>
                                        <div class="mu-radio-lbl">{{ $t(`media.upload.scope_${s}`) }}</div>
                                        <!-- ⚠ private 的文案**不能寫「只有自己看得到」**（ADR 0007）：
                                             物件其實是公開可讀的，只是不會出現在別人的素材庫裡。
                                             寫成「只有自己看得到」是給老師錯誤的安全承諾。 -->
                                        <div class="mu-radio-note">{{ $t(`media.upload.scope_${s}_note`) }}</div>
                                    </div>
                                </template>
                            </v-radio>
                        </v-radio-group>

                        <!-- ⚠ 選 public 時的說明**必須同時說出兩件事**（規格 §7.3、ADR 0005）：
                             「經審核後才會成為可用的公開素材」與「你自己仍然可以使用」。
                             只寫「審核中」會讓老師以為東西被鎖住了、不敢用。 -->
                        <div v-if="shareScope === 'public'" class="mu-review">
                            <v-icon size="18" color="#1e88e5">mdi-shield-check-outline</v-icon>
                            <div>
                                <div>{{ $t('media.upload.review_notice') }}</div>
                                <div class="mu-review-ok">{{ $t('media.upload.review_still_usable') }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- ── 權利聲明 ─────────────────────────────────────── -->
                    <div class="mu-field mu-rights">
                        <!-- ⚠ **沒勾不能送出**（決策 19）。這不是提示，是閘門。 -->
                        <v-checkbox v-model="rightsAgreed" dense hide-details class="mu-rights-box"
                            :disabled="isUploading">
                            <template #label>
                                <span class="mu-rights-lbl">{{ $t('media.upload.rights_agree') }}</span>
                            </template>
                        </v-checkbox>
                        <v-text-field v-model="rightsNote" dense outlined hide-details
                            :maxlength="NOTE_MAX" :disabled="isUploading"
                            :placeholder="$t('media.upload.rights_note_ph')" />
                        <div class="mu-hint">{{ $t('media.upload.rights_note_hint') }}</div>
                    </div>

                    <!-- 影片：上傳完不等於可用（規格 §6.3）。
                         ⚠ YouTube **不轉檔**（只登錄網址），所以不顯示這一段。 -->
                    <div v-if="kind === 'video' && !isYoutube" class="mu-processing">
                        <v-icon size="18" color="#f9a825">mdi-clock-outline</v-icon>
                        <span>{{ $t('media.upload.video_processing_notice') }}</span>
                    </div>

                    <div v-if="submitError" class="mu-err mu-err--block">{{ submitError }}</div>
                </template>
            </div>

            <div class="mu-foot">
                <v-btn text class="normal-case" :disabled="isUploading" @click="close">
                    {{ $t('button.cancel') }}
                </v-btn>
                <v-btn color="primary" depressed class="normal-case"
                    :disabled="!canSubmit" :loading="isUploading" @click="submit">
                    {{ $t('media.upload.submit') }}
                </v-btn>
            </div>
        </div>
    </v-dialog>
</template>

<script>
import { mediaServices } from '@/services/media';
import { renderPdfPoster } from '@/components/console/diary/media/pdfPoster';
import HotspotPicker from '@/components/console/diary/media/HotspotPicker';
import {
    MAX_BYTES, MAX_MB, ACCEPT, EXTNAMES, SHARE_SCOPES, VIDEO_MAX_MINUTES, USER_QUOTA_GB,
    VIDEO_MAX_LONG_EDGE, VIDEO_MAX_FPS,
    HOTSPOT_MIN_EDGE_RATIO, kindOfPurpose, isUploadable, extOf,
} from './mediaConstants';

/// 與後端 UploadMediaAction 的 StringValidator 上限一致
const TITLE_MAX = 200;
const DESC_MAX = 500;
const NOTE_MAX = 500;

/**
 * 上傳素材的對話框 — 見 mangachat-studio-api docs/plans/MediaLibrary.spec.md §7.3、§5.3。
 *
 * ⚠ **沒有 tags 欄位**（第一期不做，規格 §2.1.0）。
 *
 * ⚠ **權利聲明沒勾就不能送出**（決策 19）。
 *
 * ⚠ emit `uploaded` 時帶**整筆**回傳結果（含 `media_id` / `url` / `share_scope`），
 * 呼叫端通常要立刻把它選起來並顯示預覽，只給 id 會逼呼叫端再查一次。
 */
export default {
    name: 'MediaUploadDialog',
    components: { HotspotPicker },
    props: {
        /** 對話框開關，用 `.sync`。 */
        show: { type: Boolean, default: false },
        /** ⚠ 必填。決定大小上限、副檔名白名單，以及後端要建什麼 purpose 的列。 */
        purpose: { type: String, required: true },
        /**
         * 素材來源。`file` = 上傳檔案；`youtube` = 貼 YouTube 連結（走另一支端點）。
         *
         * ⚠ **YouTube 是「不提供貼網址」原則的唯一例外**（2026-08-12 的版權決定）。
         * 它有獨立端點是因為它不上傳、不轉檔，只把正規化過的網址登錄成一筆素材。
         * 其他任何素材都不能貼網址。
         */
        source: { type: String, default: 'file' },
    },
    data() {
        return {
            TITLE_MAX,
            DESC_MAX,
            NOTE_MAX,
            scopes: SHARE_SCOPES,
            file: null,
            dragDepth: 0,
            selectionVersion: 0,
            isValidating: false,
            /**
             * 找不同的熱區框（比例 0–1）。⚠ 只有 `purpose = 'hotspot'` 會用到。
             */
            hotspotRect: null,
            /**
             * 給熱區框選用的預覽網址（`URL.createObjectURL`）。
             * ⚠ **一定要在換檔案與關閉時 `revokeObjectURL`**，否則每選一次檔案就
             * 洩漏一份（圖片最多 10MB）留在記憶體裡直到重新載入頁面。
             */
            previewUrl: '',
            youtubeUrl: '',
            title: '',
            description: '',
            shareScope: 'private',
            rightsAgreed: false,
            rightsNote: '',
            fileError: '',
            submitError: '',
            isUploading: false,
        };
    },
    computed: {
        isYoutube() {
            return this.source === 'youtube';
        },
        /** 這是不是找不同的圖（要框熱區）。 */
        isHotspot() {
            return this.purpose === 'hotspot';
        },
        kind() {
            return kindOfPurpose(this.purpose);
        },
        uploadable() {
            /// YouTube 只適用於影片；其餘看 kind 能不能上傳（字表不行）
            return this.isYoutube ? this.kind === 'video' : isUploadable(this.purpose);
        },
        accept() {
            return ACCEPT[this.kind] || '';
        },
        /** ⚠ 不認識的 purpose 顯示原值而不是 raw key（規格 §2.5 的容忍規則）。 */
        purposeLabel() {
            const key = `media.purpose.${this.purpose}`;
            return this.$te(key) ? this.$t(key) : this.purpose;
        },
        limitHint() {
            if (this.isYoutube || !this.kind) return '';
            if (this.kind === 'video') {
                /// 影片要同時講大小與**長度**——長度才是真正會被擋的那一個（ADR 0011）
                return this.$t('media.upload.limit_video', {
                    mb: MAX_MB.video, min: VIDEO_MAX_MINUTES,
                    /// ⚠ 事前提示是老師唯一能**在開始錄影之前**知道這些限制的地方。
                    /// 尤其是 fps——前端讀不到幀率，擋不了，只能先講。
                    fps: VIDEO_MAX_FPS,
                });
            }
            return this.$t('media.upload.limit_file', {
                mb: MAX_MB[this.kind],
                ext: (EXTNAMES[this.kind] || []).join(' / '),
            });
        },
        canSubmit() {
            const hasSource = this.isYoutube
                ? this.youtubeUrl.trim().length > 0
                : !!this.file;
            /// ⚠ **找不同一定要有一個夠大的框**，否則存進去就是一張沒有熱區的素材
            /// ——那是一條死路（見 template 的說明）。
            const hotspotOk = !this.isHotspot || (
                !!this.hotspotRect
                && this.hotspotRect.w >= HOTSPOT_MIN_EDGE_RATIO
                && this.hotspotRect.h >= HOTSPOT_MIN_EDGE_RATIO);
            return this.uploadable
                && !this.isUploading
                && !this.isValidating
                && hasSource
                && hotspotOk
                && !this.fileError
                && this.title.trim().length > 0
                /// ⚠ 這一項就是決策 19 的閘門。**貼別人的 YouTube 影片時更重要**，
                /// 所以兩種來源都要勾。
                && this.rightsAgreed;
        },
    },
    watch: {
        /**
         * 每次開啟都從乾淨的狀態開始——沿用上一次的權利聲明勾選是不能接受的。
         *
         * ⚠ 與 MediaPicker 同理需要 `immediate`：呼叫端用 `v-if` 建立時，
         * 元件掛載當下 `show` 已經是 true，一般的 watcher 不會觸發。
         */
        show: {
            immediate: true,
            handler(opened) {
                if (opened) {
                    this.reset();
                } else {
                    this.clearSelection();
                }
            },
        },
    },
    /**
     * ⚠ 保險：呼叫端多半用 `v-if` 建立這個對話框，被拆掉時 `close()` 不一定跑到，
     * 那樣 objectURL 就永遠不會被釋放。Vue 2 是 `beforeDestroy`。
     */
    beforeDestroy() {
        this.clearSelection();
    },
    methods: {
        clearSelection() {
            this.selectionVersion += 1;
            this.isValidating = false;
            this.dragDepth = 0;
            this.file = null;
            this.hotspotRect = null;
            this.releasePreview();
        },

        reset() {
            this.clearSelection();
            this.youtubeUrl = '';
            this.title = '';
            this.description = '';
            this.shareScope = 'private';
            this.rightsAgreed = false;
            this.rightsNote = '';
            this.fileError = '';
            this.submitError = '';
            this.isUploading = false;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },

        /**
         * 讀影片的 metadata。讀不到回 `null`。
         *
         * ⚠ `createObjectURL` 指向本機檔案，**不會上傳任何東西**，所以這個檢查
         * 是零成本的。
         */
        readVideoMeta(file) {
            return new Promise((resolve) => {
                const url = URL.createObjectURL(file);
                const v = document.createElement('video');
                v.preload = 'metadata';
                v.onloadedmetadata = () => {
                    const meta = {
                        durationSec: v.duration,
                        width: v.videoWidth,
                        height: v.videoHeight,
                    };
                    URL.revokeObjectURL(url);
                    resolve(meta);
                };
                v.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
                v.src = url;
            });
        },

        /**
         * 影片的三道檢查。回錯誤訊息，通過回 `''`。
         *
         * ⚠⚠ **讀不到 metadata 一律放行。** Chrome 在多數設定下解不了 HEVC，
         * 而那正是 iPhone 的預設格式——把「讀不到」當成錯誤會擋掉最大宗的正常上傳。
         *
         * ⚠⚠ **三條門檻必須與後端逐字相同**，特別是「正方形放行」。
         * 前端擋了而後端沒擋只是多此一舉；**前端沒擋而後端擋了，老師才會白傳 500MB**。
         *
         * ⚠ 幀率不在這裡——`<video>` 的 metadata 沒有這個值，只能靠後端擋。
         */
        async checkVideo(file) {
            const meta = await this.readVideoMeta(file);
            if (!meta) {
                return '';
            }
            /// ⚠ `duration` 可能是 `Infinity`（metadata 不完整的錄製檔）。
            /// 直接比大小的話 `Infinity > 600` 會成立，看起來像正常擋掉，其實是誤判。
            if (Number.isFinite(meta.durationSec)
                && meta.durationSec > VIDEO_MAX_MINUTES * 60) {
                return this.$t('media.upload.err_video_long', { min: VIDEO_MAX_MINUTES });
            }
            if (!meta.width || !meta.height) {
                return '';
            }
            if (Math.max(meta.width, meta.height) > VIDEO_MAX_LONG_EDGE) {
                return this.$t('media.upload.err_video_big');
            }
            /// ⚠ 正方形（w === h）放行：它不是直式，與後端同一條規則。
            if (meta.height > meta.width) {
                return this.$t('media.upload.err_video_portrait');
            }
            return '';
        },

        pickFile() {
            if (this.isUploading) return;
            this.$refs.fileInput.value = '';
            this.$refs.fileInput.click();
        },

        isFileDrag(event) {
            const transfer = event.dataTransfer;
            return !!transfer && (
                Array.from(transfer.types || []).includes('Files')
                || Array.from(transfer.items || []).some((item) => item.kind === 'file')
                || (transfer.files && transfer.files.length > 0));
        },

        onDragEnter(event) {
            if (!this.isFileDrag(event)) return;
            event.preventDefault();
            if (this.show && this.uploadable && !this.isYoutube && !this.isUploading) {
                this.dragDepth += 1;
            }
        },

        onDragLeave(event) {
            if (!this.isFileDrag(event)) return;
            this.dragDepth = Math.max(0, this.dragDepth - 1);
        },

        onDragOver(event) {
            if (!this.isFileDrag(event)) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = this.show && this.uploadable
                && !this.isYoutube && !this.isUploading ? 'copy' : 'none';
        },

        onDrop(event) {
            this.dragDepth = 0;
            if (!this.isFileDrag(event)) return;
            event.preventDefault();
            event.stopPropagation();
            if (this.isUploading || !this.show || this.isYoutube || !this.uploadable) return;
            const transfer = event.dataTransfer;
            const items = Array.from((transfer && transfer.items) || []);
            const files = Array.from((transfer && transfer.files) || []);
            const hasDirectory = items.some((item) => {
                const entry = item.webkitGetAsEntry && item.webkitGetAsEntry();
                return entry && entry.isDirectory;
            });
            if (hasDirectory || items.some((item) => item.kind !== 'file') || !files.length) {
                this.clearSelection();
                this.fileError = this.$t('media.upload.err_drop_file');
                return;
            }
            if (files.length !== 1) {
                this.clearSelection();
                this.fileError = this.$t('media.upload.err_drop_multiple');
                return;
            }
            this.acceptFile(files[0]);
        },

        onFilePicked(event) {
            const picked = (event.target.files || [])[0];
            if (picked) this.acceptFile(picked);
        },

        async acceptFile(picked) {
            if (this.isUploading || !this.show || this.isYoutube || !this.uploadable) return;
            this.clearSelection();
            const version = this.selectionVersion;
            this.fileError = '';
            this.submitError = '';

            const limit = MAX_BYTES[this.kind];
            if (limit && picked.size > limit) {
                this.file = null;
                this.fileError = this.$t('media.upload.err_too_big', { mb: MAX_MB[this.kind] });
                return;
            }

            /// ⚠ 副檔名優先於 MIME：**HEIC 在某些瀏覽器的 file.type 是空字串**，
            /// 只看 MIME 會把 iPhone 的照片誤判成不支援。
            const allowed = EXTNAMES[this.kind] || [];
            if (allowed.length && allowed.indexOf(extOf(picked.name)) === -1) {
                this.file = null;
                this.fileError = this.$t('media.upload.err_bad_type', {
                    ext: allowed.join(' / '),
                });
                return;
            }

            /// ⚠⚠ 影片的長度／解析度／方向：**送出前先擋一次，省一趟上傳**。
            /// 後端才是權威（老師可以繞過前端直接打 API），但一支 500MB 的影片
            /// 傳完才被退回，體感差非常多。
            if (this.kind === 'video') {
                this.isValidating = true;
                let videoError;
                try {
                    videoError = await this.checkVideo(picked);
                } catch (err) {
                    // Metadata may be unreadable; the backend remains authoritative.
                    videoError = '';
                }
                if (version !== this.selectionVersion) return;
                this.isValidating = false;
                if (videoError) {
                    this.file = null;
                    this.fileError = videoError;
                    return;
                }
            }

            this.file = picked;
            /// ⚠ **換了檔案就要清掉舊的框**：座標與那張圖綁死（ADR 0004），
            /// 沿用上一張的框會產生一個指向錯誤位置的熱區，而老師看不出來。
            this.hotspotRect = null;
            if (this.isHotspot) {
                this.releasePreview();
                this.previewUrl = URL.createObjectURL(picked);
            }
            /// 標題還空著就用檔名（去副檔名）當預設，老師多半會直接用
            if (!this.title.trim()) {
                this.title = picked.name.replace(/\.[^.]+$/, '').slice(0, TITLE_MAX);
            }
        },

        async submit() {
            if (!this.canSubmit) {
                return;
            }
            this.isUploading = true;
            this.submitError = '';
            try {
                const common = {
                    purpose: this.purpose,
                    title: this.title.trim(),
                    description: this.description.trim() || null,
                    shareScope: this.shareScope,
                    rightsAgreed: this.rightsAgreed,
                    rightsNote: this.rightsNote.trim() || null,
                };
                /// ⚠ 兩支不同的端點：YouTube 不上傳檔案、不轉檔，只登錄網址。
                /// 後端會把網址正規化成 `watch?v=` 長式（`youtu.be` 短網址會被學生端
                /// 誤判成 HLS 影片），所以前端不需要、也不該自己正規化。
                /// PDF：把第一頁算繪成 JPEG 一起送上去當縮圖。
                /// ⚠ **算不出來就送 null，不擋上傳**（`renderPdfPoster` 保證不 throw）。
                /// 加密的 PDF、壞掉的檔都會走到這條，那時只是沒有縮圖。
                /// ⚠ 這一步會花幾百毫秒到一兩秒，而按鈕已經是 loading 狀態，
                /// 所以不需要額外的進度提示。
                const poster = this.kind === 'pdf' && this.file
                    ? await renderPdfPoster(this.file)
                    : null;

                const res = this.isYoutube
                    ? await mediaServices.addYoutubeMedia(
                        Object.assign({ url: this.youtubeUrl.trim() }, common))
                    : await mediaServices.uploadMedia(Object.assign({
                        file: this.file,
                        poster: poster,
                        /// ⚠ 送**比例**（0–1）而不是像素：後端會乘上它自己算出的
                        /// 輸出尺寸（縮圖與 EXIF 轉向都會改變存起來的 width/height）。
                        hotspotRect: this.hotspotRect,
                    }, common));
                this.$emit('uploaded', res);
                this.$emit('update:show', false);
            } catch (err) {
                /// ⚠ 這個 catch 不能省，而且要**分得出 403**：配額滿與參數錯的處理方式
                /// 完全不同（一個要去刪東西、一個要改欄位）。service 會把狀態碼放在
                /// err.status——那是它每一支都傳 handleError 換來的。
                this.submitError = this.errorMessage(err);
            } finally {
                this.isUploading = false;
            }
        },

        errorMessage(err) {
            const status = err && err.status;
            if (status === 403) {
                return this.$t('media.upload.err_quota', { gb: USER_QUOTA_GB });
            }
            if (status === 400) {
                /// 400 的原因很多（檔案格式、影片太長、權利聲明沒帶到），
                /// 後端訊息是英文的技術描述，不適合直接給老師看，但要留在 console 裡可查。
                console.log('uploadMedia rejected:', err && err.message);
                /// YouTube 的 400 幾乎一定是「網址認不出來」，講得具體一點
                return this.isYoutube
                    ? this.$t('media.upload.err_bad_youtube')
                    : this.$t('media.upload.err_rejected');
            }
            return this.$t('media.upload.err_failed');
        },

        close() {
            if (this.isUploading) {
                return;
            }
            this.clearSelection();
            this.$emit('update:show', false);
        },

        /**
         * 釋放 `URL.createObjectURL` 產生的網址。
         * ⚠ **不釋放的話每選一次檔案就洩漏一份**（圖片最多 10MB），留在記憶體裡
         * 直到重新載入頁面。老師換三四張圖挑熱區是常態。
         */
        releasePreview() {
            if (this.previewUrl) {
                URL.revokeObjectURL(this.previewUrl);
                this.previewUrl = '';
            }
        },
    },
};
</script>

<style scoped>
.mu {
    position: relative;
    background: #fff;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    max-height: 88vh;
}

.mu-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 16px 20px 12px;
    border-bottom: 1px solid #eceff1;
}

.mu-title {
    font-size: 17px;
    font-weight: 600;
    color: #263238;
}

.mu-sub {
    font-size: 12px;
    color: #78909c;
    margin-top: 2px;
}

.mu-body {
    padding: 16px 20px;
    overflow-y: auto;
}

.mu-field {
    margin-bottom: 18px;
}

.mu-lbl {
    font-size: 13px;
    font-weight: 600;
    color: #455a64;
    margin-bottom: 6px;
}

.mu-req {
    color: #e53935;
}

.mu-drop {
    border: 2px dashed #b0bec5;
    border-radius: 6px;
    padding: 18px;
    background: #fafcfd;
}

.mu-drop-overlay {
    position: absolute;
    inset: 0;
    z-index: 10;
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px dashed #1976d2;
    border-radius: 8px;
    background: rgba(227, 242, 253, 0.85);
    color: #1565c0;
    font-weight: 600;
}

.mu-drop-overlay span {
    padding: 12px 20px;
    border-radius: 6px;
    background: #fff;
}

.mu-drop--disabled {
    opacity: 0.6;
}

.mu-drop-hint {
    font-size: 13px;
    color: #455a64;
    margin-bottom: 12px;
}

.mu-file {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.mu-file-name {
    font-size: 13px;
    color: #263238;
    word-break: break-all;
}

.mu-hint {
    font-size: 12px;
    color: #90a4ae;
    margin-top: 4px;
}

.mu-cnt {
    font-size: 11px;
    color: #b0bec5;
    text-align: right;
    margin-top: 2px;
}

.mu-err {
    font-size: 12px;
    color: #e53935;
    margin-top: 6px;
}

.mu-err--block {
    padding: 8px 10px;
    background: #ffebee;
    border-radius: 4px;
}

.mu-radios {
    margin-top: 0;
}

.mu-radio-lbl {
    font-size: 13px;
    color: #263238;
}

.mu-radio-note {
    font-size: 11px;
    color: #90a4ae;
    line-height: 1.4;
}

.mu-review {
    display: flex;
    gap: 8px;
    padding: 10px 12px;
    background: #e3f2fd;
    border-radius: 4px;
    font-size: 12px;
    color: #37474f;
    line-height: 1.5;
    margin-top: 6px;
}

.mu-review-ok {
    color: #1565c0;
    font-weight: 600;
    margin-top: 2px;
}

.mu-rights {
    padding: 12px;
    background: #fafafa;
    border-radius: 4px;
}

.mu-rights-box {
    margin-top: 0;
    margin-bottom: 8px;
}

.mu-rights-lbl {
    font-size: 13px;
    color: #263238;
    line-height: 1.5;
}

.mu-processing {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #795548;
    background: #fff8e1;
    border-radius: 4px;
    padding: 8px 10px;
}

.mu-blocked {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #455a64;
    padding: 20px 0;
}

.mu-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 20px;
    border-top: 1px solid #eceff1;
}
</style>
